import Alert from '~/components/alert';
import Submit from '~/components/button-submit';
import Card from '~/components/card';
import Form from '~/components/form';
import Input from '~/components/form-controls/input';
import Icon from '~/components/icon';
import LinkButton from '~/components/link-button';
import Page from '~/components/page';
import { useIsLoading } from '~/utils/routing';
import {
  FormKeys,
  useCrewOrderLookupActionResult,
  useCrewOrderLookupData,
  useOrderLookupActionResult,
} from './orderLookup';

function OrderLookupDetailText({ text }: { text: string | undefined }) {
  return text ?
      <div className="flex items-start gap-2 border-none text-gray-800">
        <Icon icon="InfoCircle" className="size-6 shrink-0" />
        {text}
      </div>
    : null;
}

function ErrorMessage({ message }: { message?: string }) {
  return message ?
      <Alert variant="error" title="Something isn't quite right">
        <p>{message}</p>
      </Alert>
    : null;
}

function RegistrationLink({ linkText }: { linkText?: string }) {
  return linkText ?
      <LinkButton to="register" variant="text" className="px-0">
        {linkText}
      </LinkButton>
    : null;
}

type OrderLookupPageProps = {
  orderLookupDetailText?: string;
  orderNumberDescription?: string;
  orderLookupLabel?: string;
  startRegistrationText?: string;
  errorMessage?: string;
};

function OrderLookup(props: OrderLookupPageProps) {
  const isLoading = useIsLoading();
  const {
    orderLookupDetailText,
    orderNumberDescription,
    orderLookupLabel = 'Order Number',
    startRegistrationText,
    errorMessage,
  } = props;

  return (
    <Page headline="Let's find your order">
      <Card className="items-start">
        <OrderLookupDetailText text={orderLookupDetailText} />
        <Form className="w-full items-start">
          <Input
            id={FormKeys.EmailOrPostalCode}
            name={FormKeys.EmailOrPostalCode}
            label="Email Address or Postal Code"
            autoComplete="email postal-code"
            required
            validationMessages={{
              valueMissing: 'Please enter your email address or postal code.',
            }}
          />

          <Input
            id={FormKeys.OrderNo}
            name={FormKeys.OrderNo}
            label={orderLookupLabel}
            description={`${orderNumberDescription ?? 'E.g. 123456, RS-98765, etc.'}`}
            required
            validationMessages={{
              valueMissing: 'Please enter your order number.',
            }}
          />

          <ErrorMessage message={errorMessage} />

          <Submit loading={isLoading}>Look It Up</Submit>
        </Form>

        <RegistrationLink linkText={startRegistrationText} />
      </Card>
    </Page>
  );
}

export function BasicOrderLookupPage() {
  const actionResult = useOrderLookupActionResult();
  const errorMessage =
    actionResult?.ok === false ? actionResult.message : undefined;

  return <OrderLookup errorMessage={errorMessage} />;
}

export function BrandedOrderLookupPage() {
  const actionResult = useCrewOrderLookupActionResult();
  const {
    orderNumberDescription,
    orderLookupDetailText,
    orderLookupLabel,
    startRegistrationText,
  } = useCrewOrderLookupData();
  const errorMessage =
    actionResult?.ok === false ? actionResult.message : undefined;

  return (
    <OrderLookup
      errorMessage={errorMessage}
      orderNumberDescription={orderNumberDescription}
      orderLookupDetailText={orderLookupDetailText}
      orderLookupLabel={orderLookupLabel}
      startRegistrationText={startRegistrationText}
    />
  );
}
